"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Mission() {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const paragraphs = [{
    text: "In the silent vacuum of orbital operations, data is the only currency that matters. TiaLinx, Inc. transforms raw electromagnetic signals into actionable intelligence, providing the backbone for secure, high-fidelity aerospace sensing in contested environments."
  }, {
    text: "Our systems are engineered for the extreme. By integrating advanced signal processing with hardened hardware architectures, we deliver persistent, real-time situational awareness that allows our partners to command the high ground with absolute technical certainty."
  }];
  return <section data-section-id="3020" id="the-tialinx-mission" className="bg-[#080F1A] text-[#B2BDC7] py-32 md:py-[200px] px-4 md:px-8 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {}
          <div ref={imageRef} className="relative">
            <div className="relative">
              {}
             <div className="absolute -top-3 -left-3 -right-3 -bottom-3 border pointer-events-none transition-opacity duration-1000" style={{
              borderColor: "#6697B9",
              opacity: imageVisible ? 0.6 : 0,
              transitionDelay: "800ms"
            }} />

              {}
              <div className="relative flex justify-center items-center overflow-hidden w-full" style={{
              clipPath: imageVisible ? "inset(0% 0% 0% 0%)" : "inset(100% 0% 0% 0%)",
              transition: "clip-path 1400ms cubic-bezier(0.22, 1, 0.36, 1)"
            }}>
  <img src="https://tialinx.com/wp-content/uploads/2026/07/ChatGPT-Image-Jul-7-2026-07_25_04-PM.png" alt="Advanced orbital sensor array" loading="lazy" decoding="async" className="block w-[92%] max-w-[900px] h-auto object-contain mx-auto" style={{
                filter: "grayscale(0.5) contrast(1.2)"
              }} />
</div>
              </div>

            {}
            <div className="mt-6 flex items-center gap-3 transition-all duration-1000" style={{
            opacity: imageVisible ? 1 : 0,
            transform: imageVisible ? "translateY(0)" : "translateY(12px)",
            transitionDelay: "1200ms"
          }}>
              <span className="h-px w-8" style={{
              backgroundColor: "#6697B9"
            }} aria-hidden="true" />
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase" style={{
              color: "#45596F"
            }}>ORBITAL COMMAND</span>
            </div>
          </div>

          {}
          <div ref={textRef} className="lg:pl-12 xl:pl-20">
            {}
            <div className="transition-all duration-700 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)"
          }}>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase mb-10" style={{
              color: "#6697B9"
            }}>
                — Mission Brief · Intelligence
              </p>
            </div>

            {}
            <h2 className="font-default font-semibold leading-[1.1] text-4xl md:text-5xl lg:text-[56px] mb-12 transition-all duration-700 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "80ms",
            color: "#B2BDC7"
          }}>
              Commanding the
              <br />
              <em className="not-italic font-semibold" style={{
              color: "#6697B9"
            }}>
                orbital frontier.
              </em>
            </h2>

            {}
            <div className="h-px w-16 mb-10 transition-all duration-700 ease-out" style={{
            backgroundColor: "#6697B9",
            opacity: textVisible ? 0.7 : 0,
            transform: textVisible ? "scaleX(1)" : "scaleX(0)",
            transformOrigin: "left",
            transitionDelay: "160ms"
          }} aria-hidden="true" />

            {}
            <div className="space-y-7">
              {paragraphs.map((para, i) => <p data-index={i} key={i} className="font-default text-[18px] leading-[1.6] transition-all duration-700 ease-out" style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: `${240 + i * 80}ms`,
              color: "#B2BDC7"
            }}>
                  {i === 0 ? <>
                      <span className="float-left font-default font-bold mr-3 mt-1 leading-[0.85]" style={{
                  fontSize: "72px",
                  color: "#6697B9"
                }}>
                        I
                      </span>
                      {para.text.substring(1)}
                    </> : para.text}
                </p>)}
            </div>

            {}
            <div className="mt-14 lg:ml-16 lg:pl-8 border-l transition-all duration-700 ease-out" style={{
            borderColor: "#6697B9",
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "480ms"
          }}>
              <blockquote className="font-default text-2xl md:text-[24px] leading-[1.5] mb-4" style={{
              color: "#B2BDC7"
            }}>
                « Precision is the ultimate form of authority in space. »
              </blockquote>
              <cite className="not-italic font-mono text-[11px] tracking-[0.2em] uppercase" style={{
              color: "#45596F"
            }}>
                TiaLinx Command · Systems Engineering
              </cite>
            </div>

            {}
            <div className="mt-16 transition-all duration-700 ease-out" style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "560ms"
          }}>
              <Button asChild variant="ghost" className="group h-auto px-0 py-2 font-default text-base hover:bg-transparent hover:text-[#6697B9]">
                <Link to="mailto:info@tialinx.com" className="inline-flex items-center gap-3">
                  <span className="font-mono text-[12px] tracking-[0.25em] uppercase border-b pb-1" style={{
                  borderColor: "#45596F"
                }}>
                    View Mission Profile
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-500 group-hover:translate-x-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}